import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import type {
	AddHelpdeskNoteBody,
	CreateHelpdeskTicketBody,
	HelpdeskTicketDetail,
	HelpdeskTicketsFilter,
	HelpdeskTicketsPage,
	PatchHelpdeskTicketBody,
} from "../../components/helpdesk/types";
import { api } from "./client";
import { queryClient } from "./query-client";

/**
 * Helpdesk (M11, PLAN §3.2): one queue of tickets with categories (ROOM in
 * M11). Every read is one request the API answers with one statement — the
 * list is keyset on `(created_at, helpdesk_ticket_id)` or `(priority_rank, …)` with
 * the total on the first page. Who sees which ticket is decided in SQL
 * (`ticketVisible`): admins all, a host desk by organisation scope, a HOTEL
 * by its buildings, a NOC by its own organisation. Keys live under
 * `["helpdesk", …]`, so one invalidate refreshes the desk list, the HOTEL's
 * and the NOC's "Room problems".
 */

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

export const helpdeskKeys = {
	all: ["helpdesk"] as const,
	tickets: (f: HelpdeskTicketsFilter) => ["helpdesk", "tickets", clean(f)] as const,
	ticket: (id: string) => ["helpdesk", "ticket", id] as const,
};

/** `GET /helpdesk/tickets` — keyset; `status` is the comma list (absent = every status). */
export const helpdeskTicketsOptions = (f: HelpdeskTicketsFilter) =>
	queryOptions({
		queryKey: helpdeskKeys.tickets(f),
		queryFn: ({ signal }) => api.get<HelpdeskTicketsPage>("/helpdesk/tickets", { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /helpdesk/tickets/:id` — the ticket and its notes in one statement (`json_agg`, ≤ 200). */
export const helpdeskTicketOptions = (id: string) =>
	queryOptions({
		queryKey: helpdeskKeys.ticket(id),
		queryFn: ({ signal }) => api.get<HelpdeskTicketDetail>(`/helpdesk/tickets/${id}`, { signal }),
		staleTime: 15_000,
	});

export const createHelpdeskTicket = (body: CreateHelpdeskTicketBody) => api.post<HelpdeskTicketDetail>("/helpdesk/tickets", body);

export const patchHelpdeskTicket = (id: string, body: PatchHelpdeskTicketBody) => api.patch<HelpdeskTicketDetail>(`/helpdesk/tickets/${id}`, body);

/** A note, and with it an optional status change, in one transaction (the trigger guards the graph). */
export const addHelpdeskNote = (id: string, body: AddHelpdeskNoteBody) => api.post<HelpdeskTicketDetail>(`/helpdesk/tickets/${id}/notes`, body);

export const invalidateHelpdesk = () => queryClient.invalidateQueries({ queryKey: helpdeskKeys.all });

